import { PlaceName } from '../../types.ts';
import { fetchWithTimeout, parseErrorResponse, OfflineQueue } from './base.ts';

/** #1666 — slechtste geval van één plaatsgroep, zie bulkGeocodePlaces. */
const GEOCODE_GROEP_TIMEOUT = 150_000;

export type MergeUitzondering =
  | { eventId: string; placeId: string }
  | { personId: string; veld: 'birth' | 'baptism' | 'death' | 'burial'; placeId: string };

/** NC #815 — een plaats in het project die de akte-plaats kan zijn. */
export interface PlaatsKandidaat {
  id: string;
  name: string;
  state: string | null;
  country: string | null;
}

/** Zie repos/places.js `zoekPlaatsKandidaten` voor de beslisregel. */
export interface PlaatsKandidatenUitkomst {
  /** De naam die een nieuwe plaats zou krijgen ("Achthoven, Utrecht, Nederland"). */
  naam: string;
  voorstel: PlaatsKandidaat | null;
  kandidaten: PlaatsKandidaat[];
  reden: 'exact' | 'provincie' | 'enig' | 'meerdere' | 'geen';
}

export const placesApi = {
  /** NC #815 — kandidaten voor een plaats uit een akte (archiefwizard). */
  getPlaatsKandidaten: async (
    projectId: string,
    delen: { plaats: string; provincie?: string; land?: string }
  ): Promise<PlaatsKandidatenUitkomst> => {
    const q = new URLSearchParams({ plaats: delen.plaats });
    if (delen.provincie) q.set('provincie', delen.provincie);
    if (delen.land) q.set('land', delen.land);
    const res = await fetchWithTimeout(`/api/projects/${projectId}/places/kandidaten?${q}`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  getPlaces: async (projectId: string): Promise<any[]> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/places`);
      if (!res.ok) await parseErrorResponse(res);
      const rows = await res.json();
      // Map DB columns to frontend Place interface
      return rows.map((p: any) => ({
        id: p.id,
        projectId: p.project_id,
        name: p.name,
        lat: p.latitude ? parseFloat(p.latitude) : null,
        lng: p.longitude ? parseFloat(p.longitude) : null,
        verified: p.geocode_status === 'success' || p.geocode_status === 'manual',
        needsReview: p.geocode_status === 'failed',
        events: parseInt(p.event_count) || 0,
        source: 'GEDCOM' as const,
        // Address components from Nominatim
        houseNumber: p.house_number || null,
        road: p.road || null,
        village: p.village || null,
        municipality: p.municipality || null,
        state: p.state || null,
        zipCode: p.zip_code || null,
        country: p.country || null,
        geocodeStatus: p.geocode_status || null,
        geocodeTerugval: p.geocode_terugval || null,
        parentPlaceId: p.parent_place_id || null,
      }));
    } catch (error) {
      console.warn('⚠️ Database unavailable, returning empty places list:', error);
      return [];
    }
  },

  registerPlace: async (projectId: string, name: string, needsReview: boolean): Promise<any> => {
    try {
      const res = await fetchWithTimeout(`/api/projects/${projectId}/places`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, needsReview }),
      });
      if (!res.ok) await parseErrorResponse(res);
      return await res.json();
    } catch (error) {
      // #1359 — deze aanroep werd hier stilgelegd in de offline-queue en gaf
      // `null` terug. De aanroeper koppelt dan niets, dus wat de queue later
      // aanmaakt is een plaats waar geen enkele gebeurtenis naar verwijst:
      // dode data die bij een GEDCOM-import met duizenden plaatsen ook nog eens
      // alle andere wachtende bewerkingen uit de gecapte queue duwde.
      // De aanroeper hoort de fout te zien en te melden.
      console.warn('Plaats registreren mislukt:', name, error);
      throw error;
    }
  },

  bulkGeocodePlaces: async (
    projectId: string,
    placeNames: string[]
  ): Promise<{
    mapping: Record<string, string>;
    stats: { total: number; created: number; existing: number; failed: number };
  }> => {
    try {
      const res = await fetchWithTimeout(
        `/api/projects/${projectId}/places/bulk-geocode`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ places: placeNames }),
        },
        // #1666 — een groep van 25 met 4 tegelijk kost bij een onbereikbare
        // Photon (15 s per aanvraag) ±105 s. Geeft de browser eerder op, dan
        // registreert de client de plaatsen terwijl de server de groep nog
        // afwerkt → dubbele rijen. Dus ruim boven het slechtste geval.
        GEOCODE_GROEP_TIMEOUT
      );
      if (!res.ok) await parseErrorResponse(res);
      return await res.json();
    } catch (error) {
      console.error('Bulk geocode places failed:', error);
      return {
        mapping: {},
        stats: { total: placeNames.length, created: 0, existing: 0, failed: placeNames.length },
      };
    }
  },

  searchPlaces: async (projectId: string, query: string): Promise<any[]> => {
    if (query.length < 2) return [];
    try {
      const res = await fetchWithTimeout(
        `/api/projects/${projectId}/places/search?q=${encodeURIComponent(query)}`
      );
      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  searchNominatim: async (projectId: string, query: string): Promise<any[]> => {
    if (query.length < 3) return [];
    try {
      const res = await fetchWithTimeout(
        `/api/projects/${projectId}/places/nominatim-search?q=${encodeURIComponent(query)}`
      );
      if (!res.ok) return [];
      return await res.json();
    } catch {
      return [];
    }
  },

  createPlaceFromNominatim: async (
    projectId: string,
    result: {
      name: string;
      displayName?: string;
      latitude: number;
      longitude: number;
      country?: string;
      state?: string;
      municipality?: string;
      street?: string;
      housenumber?: string;
      osmId?: string | null;
      /** #1561 (NC #795) — 'place' (populated place) vs 'boundary' (gemeente/provincie). */
      osmKey?: string | null;
      countryCode?: string | null;
      stateCode?: string | null;
    }
  ): Promise<any> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/places`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: result.name,
        display_name: result.displayName || null,
        latitude: result.latitude,
        longitude: result.longitude,
        country: result.country || null,
        state: result.state || null,
        municipality: result.municipality || null,
        // #1561 — een 'place' (osm_key) is een nederzetting, geen bestuurlijke
        // grens; zonder dit veld liet de plaatskiezer een nieuw aangemaakte
        // plaats zonder village-koppeling achter (kon niet als 'Plaats'
        // herkend worden bij een latere municipality/state-match).
        village: result.osmKey === 'place' ? result.name : null,
        road: result.street || null,
        house_number: result.housenumber || null,
        country_code: result.countryCode || null,
        state_code: result.stateCode || null,
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  },

  regeocodePlace: async (placeId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/places/${placeId}/regeocode`, {
      method: 'POST',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  triggerGeocoding: async (projectId: string): Promise<void> => {
    await fetchWithTimeout(`/api/projects/${projectId}/places/geocode-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
  },

  updatePlace: async (id: string, updates: any): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/places/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'UPDATE',
        endpoint: `/api/places/${id}`,
        method: 'PUT',
        data: updates,
      });
    }
  },

  /**
   * #1485 — de groep uit de dubbelen-banner in één transactie. `uitzonderingen`
   * zijn de rijen die níet naar target gaan: een events-rij (`eventId`) of een
   * vitaal persoonsveld (`personId` + `veld`). Een bronplaats die daarna nog
   * iets vasthoudt blijft bestaan; de server meldt wat er echt gebeurd is.
   */
  mergePlaceGroup: async (
    targetId: string,
    sourceIds: string[],
    uitzonderingen: MergeUitzondering[]
  ): Promise<{ updated: number; verwijderd: string[]; behouden: string[] }> => {
    const res = await fetchWithTimeout(`/api/places/${targetId}/merge-group`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceIds, uitzonderingen }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  mergePlaces: async (targetId: string, sourceId: string): Promise<{ updated: number }> => {
    const res = await fetchWithTimeout(`/api/places/${targetId}/merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceId }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  reverseGeocode: async (
    projectId: string,
    lat: number,
    lng: number
  ): Promise<{
    name: string;
    displayName: string;
    latitude: number;
    longitude: number;
    municipality?: string;
    state?: string;
    country?: string;
  }> => {
    const res = await fetchWithTimeout(
      `/api/projects/${projectId}/places/reverse-geocode?lat=${lat}&lng=${lng}`
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * NC #864 — ongebruikte plaatsen in één transactie weg. De server laat staan
   * wat nog ergens aan hangt en meldt per id wat er gebeurde. Geen offline-
   * wachtrij: de gebruiker moet zien wat er echt verwijderd is.
   */
  ruimOngebruiktePlaatsenOp: async (
    projectId: string,
    placeIds: string[]
  ): Promise<{ verwijderd: string[]; behouden: string[] }> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/places/opruimen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placeIds }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * NC #864 keuze B — het voorstel van de server: wat weg kan en wat blijft
   * staan, met reden ('handmatig' of 'naam' met de losse tekst en waar die staat).
   */
  opruimVoorstel: async (
    projectId: string
  ): Promise<{
    verwijderbaar: { id: string; name: string }[];
    behouden: {
      id: string;
      name: string;
      reden: 'handmatig' | 'naam';
      tekst?: string;
      bron?: 'gebeurtenis' | 'persoon';
    }[];
  }> => {
    const res = await fetchWithTimeout(`/api/projects/${projectId}/places/opruimen`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  deletePlace: async (id: string): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/places/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'DELETE',
        endpoint: `/api/places/${id}`,
        method: 'DELETE',
        data: {},
      });
    }
  },

  // --- Place names (historische namen) ---

  getPlaceNames: async (placeId: string): Promise<PlaceName[]> => {
    const res = await fetchWithTimeout(`/api/places/${placeId}/names`);
    if (!res.ok) throw new Error(await parseErrorResponse(res));
    return res.json();
  },

  addPlaceName: async (placeId: string, data: Partial<PlaceName>): Promise<PlaceName> => {
    const res = await fetchWithTimeout(`/api/places/${placeId}/names`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await parseErrorResponse(res));
    return res.json();
  },

  updatePlaceName: async (nameId: string, data: Partial<PlaceName>): Promise<PlaceName> => {
    const res = await fetchWithTimeout(`/api/place-names/${nameId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await parseErrorResponse(res));
    return res.json();
  },

  deletePlaceName: async (nameId: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/place-names/${nameId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await parseErrorResponse(res));
  },
};
